import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";

import Section from "../layout/Section";
import Container from "../layout/Container";

const reviews = [
  {
    name: "Temi",
    role: "Guest",
    text: "Bright, comfortable and exactly like the photos. Zenia jumped in quickly when we needed help at check-in.",
  },
  {
    name: "Carmen",
    role: "Family Stay",
    text: "A beautiful stay with very comfortable beds. My family loved the home and the attention throughout.",
  },
  {
    name: "Laura",
    role: "Weekend Stay",
    text: "Everything felt fresh, clean and very well kept. The rooftop and location made the stay even better.",
  },
  {
    name: "Marco",
    role: "First-Time Guest",
    text: "The home felt special from the moment we arrived. Great setup, beautiful rooms and a very attentive host.",
  },
  {
    name: "Nina",
    role: "Guest",
    text: "Spacious, easy to reach and well prepared. We had everything we needed and would stay here again.",
  },
  {
    name: "Camilla",
    role: "Guest",
    text: "The place was beautiful, clean and calm. Communication was prompt and the whole stay felt effortless.",
  },
  {
    name: "Jasmine",
    role: "Anniversary Stay",
    text: "Even with tricky weather, the home was beautiful and the host was extremely responsive.",
  },
  {
    name: "Mari",
    role: "Guest",
    text: "Great communication, comfortable rooms and a stay that felt smooth from start to finish.",
  },
  {
    name: "Jordan",
    role: "Guest",
    text: "The place looked just like the photos and the host replied incredibly fast. Very personable experience.",
  },
  {
    name: "Eleanor",
    role: "Guest",
    text: "Cozy, fresh and very welcoming. You could tell the suite had been prepared with care.",
  },
  {
    name: "Wakeel",
    role: "Guest",
    text: "Everything was smooth, clear and stress-free. Zenia was very responsive and made the stay easy.",
  },
  {
    name: "Michel Yostin",
    role: "Guest",
    text: "Clean home, quick replies and a quiet area close to everything. A wonderful experience overall.",
  },
  {
    name: "Deivid",
    role: "Guest",
    text: "Very well located and thoughtfully set up. The place was exactly as described, which I really appreciated.",
  },
];

function initials(name) {
  return name
    .split(" ")
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();
}

function ReviewCard({ review }) {
  const seed = review.name.length % 3;

  return (
    <div
      className="
        rounded-[24px] border border-white/10
        bg-white/96 p-5 shadow-soft
        backdrop-blur
      "
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <div
            className={`
              flex h-11 w-11 shrink-0 items-center justify-center rounded-full
              text-[12px] font-semibold text-white
              ${
                seed === 0
                  ? "bg-gradient-to-br from-brand-primary to-brand-secondary"
                  : seed === 1
                    ? "bg-gradient-to-br from-accent-gold to-brand-primary"
                    : "bg-gradient-to-br from-brand-secondary to-accent-gold"
              }
            `}
          >
            {initials(review.name)}
          </div>

          <div>
            <div className="text-[15px] font-semibold leading-tight text-text-primary">
              {review.name}
            </div>
            <div className="text-[12px] text-text-muted">{review.role}</div>
          </div>
        </div>

        <div className="text-[12px] tracking-[0.2em] text-accent-gold">
          ★★★★★
        </div>
      </div>

      <p className="mt-4 text-[14px] leading-relaxed text-text-muted">
        {review.text}
      </p>
    </div>
  );
}

export default function ReviewsWall() {
  const columns = useMemo(() => {
    return [
      reviews.filter((_, i) => i % 3 === 0),
      reviews.filter((_, i) => i % 3 === 1),
      reviews.filter((_, i) => i % 3 === 2),
    ];
  }, []);

  const repeatedColumns = columns.map((col) => [...col, ...col, ...col]);

  return (
    <Section
      background="bg-brand-secondary"
      padding="none"
      className="relative overflow-hidden py-14 md:py-18 lg:py-20"
    >
      {/* background glow */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(255,255,255,0.12),transparent_55%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom,rgba(212,175,55,0.20),transparent_55%)]" />
        <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(255,255,255,0.03),rgba(255,255,255,0))]" />
      </div>

      <Container>
        {/* top heading - mobile friendly */}
        <div className="mx-auto max-w-2xl text-center">
          <p className="text-small font-semibold uppercase tracking-[0.2em] text-white/60">
            Guest experience
          </p>

          <h2 className="mt-3 text-[28px] font-heading font-extrabold leading-[1.05] text-white md:text-[40px]">
            Everything they say about Zenia
          </h2>

          <p className="mt-4 text-body text-white/70">
            Real feedback from guests and owners, shown in a compact wall so the section feels alive without getting too tall.
          </p>
        </div>

        {/* wall */}
        <div className="relative mt-10 md:mt-12">
          {/* center text badge for desktop */}
          <div className="pointer-events-none absolute left-1/2 top-1/2 z-20 hidden -translate-x-1/2 -translate-y-1/2 lg:flex">
            <div
              className="
                max-w-[320px] rounded-[28px]
                border border-white/10 bg-brand-secondary/80
                px-6 py-5 text-center backdrop-blur-md shadow-card
              "
            >
              <div className="text-small font-semibold uppercase tracking-[0.18em] text-white/60">
                Guest experience
              </div>
              <div className="mt-2 text-[24px] font-heading font-extrabold leading-tight text-white">
                Everything they say about Zenia
              </div>
            </div>
          </div>

          {/* mobile: swipe row */}
          <div className="md:hidden">
            <div className="no-scrollbar flex snap-x snap-mandatory gap-4 overflow-x-auto pb-2">
              {reviews.map((review) => (
                <div key={review.name} className="min-w-[84vw] snap-start">
                  <ReviewCard review={review} />
                </div>
              ))}
            </div>

            <div className="mt-5 text-center text-small text-white/60">
              Swipe to read more
            </div>
          </div>

          {/* desktop: contained loop wall */}
          <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-3 gap-5 h-[560px] overflow-hidden">
            {repeatedColumns.map((column, colIndex) => (
              <motion.div
                key={colIndex}
                className="flex h-full flex-col gap-5 will-change-transform"
                animate={{ y: ["0%", "-50%"] }}
                transition={{
                  duration: 20 + colIndex * 4,
                  repeat: Infinity,
                  ease: "linear",
                }}
              >
                {column.map((review, idx) => (
                  <div key={`${review.name}-${idx}-${colIndex}`}>
                    <ReviewCard review={review} />
                  </div>
                ))}
              </motion.div>
            ))}
          </div>
        </div>
      </Container>
    </Section>
  );
}